import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import {
  ReferralsRepositoryService,
  ReferralTargetsRepositoryService,
  ReferralTarget,
  Referral,
  ReferralMessage,
  ReferralStatus,
  CreateReferralRequest,
  AcceptReferralRequest,
  AcceptReferralResult,
  AddReferralMessageRequest,
  UpdateReferralMessageRequest,
  PracticeReferralPatientSnapshot,
  CreateReferralSnapshotRequest,
  WebSocketService,
  Events
} from '@xmedical/core';
import { CoreEvent, CoreEventsService } from '../services/core-events.service';
import { CoreService } from '../services/core.service';
import { PracticeStore } from './practice.store';

@Injectable({
  providedIn: 'root'
})
export class ReferralStore {
  private referralsRepo = inject(ReferralsRepositoryService);
  private referralTargetsRepo = inject(ReferralTargetsRepositoryService);
  private events = inject(CoreEventsService);
  private coreService = inject(CoreService);
  private websocket = inject(WebSocketService);
  private practiceStore = inject(PracticeStore);

  // State signals
  readonly incomingReferrals = signal<Referral[]>([]);
  readonly outgoingReferrals = signal<Referral[]>([]);
  readonly selectedReferral = signal<Referral | null>(null);
  readonly patientReferrals = signal<Referral[]>([]);
  readonly referralSnapshots = signal<PracticeReferralPatientSnapshot[]>([]);
  readonly referralTargets = signal<ReferralTarget[]>([]);
  readonly loading = signal<boolean>(false);
  readonly loaded = signal<boolean>(false);

  // Computed signals
  readonly pendingIncoming = computed(() =>
    this.incomingReferrals().filter(r => r.status === ReferralStatus.Pending)
  );

  readonly acceptedIncoming = computed(() =>
    this.incomingReferrals().filter(r => r.status === ReferralStatus.Accepted)
  );

  readonly pendingOutgoing = computed(() =>
    this.outgoingReferrals().filter(r => r.status === ReferralStatus.Pending)
  );

  readonly pendingIncomingCount = computed(() => this.pendingIncoming().length);

  readonly selectedPracticeId = computed(() => this.practiceStore.selectedPracticeId());

  private readonly practiceEventsChannelName = computed(() =>
    this.practiceStore.practiceEventsChannelName()
  );

  /**
   * Load all referrals (incoming + outgoing) for the current practice
   */
  loadAll(): Observable<void> {
    this.loading.set(true);

    return forkJoin({
      incoming: this.referralsRepo.getIncoming(),
      outgoing: this.referralsRepo.getOutgoing()
    }).pipe(
      tap(({ incoming, outgoing }) => {
        this.incomingReferrals.set(incoming);
        this.outgoingReferrals.set(outgoing);
        this.loaded.set(true);
        this.loading.set(false);
        this.events.trigger(new CoreEvent('referrals_loaded'));
      }),
      map(() => void 0)
    );
  }

  /**
   * Load incoming referrals only
   */
  loadIncoming(): Observable<Referral[]> {
    return this.referralsRepo.getIncoming().pipe(
      tap(referrals => {
        this.incomingReferrals.set(referrals);
      })
    );
  }

  /**
   * Load outgoing referrals only
   */
  loadOutgoing(): Observable<Referral[]> {
    return this.referralsRepo.getOutgoing().pipe(
      tap(referrals => {
        this.outgoingReferrals.set(referrals);
      })
    );
  }

  /**
   * Load a single referral by ID
   */
  loadReferral(id: number): Observable<Referral> {
    return this.referralsRepo.getByIdWithPatient(id).pipe(
      tap(referral => {
        this.selectedReferral.set(referral);
      })
    );
  }

  /**
   * Load referrals for a specific patient (filters from loaded data)
   */
  loadPatientReferrals(patientId: number): void {
    const incoming = this.incomingReferrals().filter(
      r => r.receivingPatientId === patientId || r.referringPatientId === patientId
    );
    const outgoing = this.outgoingReferrals().filter(
      r => r.referringPatientId === patientId
    );
    const combined = [...incoming, ...outgoing];
    // Deduplicate by ID
    const unique = combined.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
    this.patientReferrals.set(unique);
  }

  /**
   * Load available referral targets for the current practice
   */
  loadTargets(): Observable<ReferralTarget[]> {
    return this.referralTargetsRepo.getTargets().pipe(
      tap(targets => {
        this.referralTargets.set(targets);
      })
    );
  }

  /**
   * Create a new referral
   */
  createReferral(request: CreateReferralRequest): Observable<number> {
    return this.referralsRepo.create(request).pipe(
      tap(() => {
        // Reload outgoing referrals to include the new one
        this.loadOutgoing().subscribe();
      })
    );
  }

  /**
   * Accept an incoming referral
   */
  acceptReferral(id: number, request: AcceptReferralRequest): Observable<AcceptReferralResult> {
    return this.referralsRepo.accept(id, request).pipe(
      tap(() => {
        // Update the referral status in local state
        this.updateReferralStatus(id, ReferralStatus.Accepted);
      })
    );
  }

  /**
   * Reject an incoming referral
   */
  rejectReferral(id: number): Observable<boolean> {
    return this.referralsRepo.reject(id).pipe(
      tap(success => {
        if (success) {
          this.updateReferralStatus(id, ReferralStatus.Rejected);
        }
      })
    );
  }

  /**
   * Add a message to a referral
   */
  addMessage(referralId: number, request: AddReferralMessageRequest): Observable<ReferralMessage> {
    return this.referralsRepo.addMessage(referralId, request).pipe(
      tap(message => {
        // Update selected referral messages if it's the current one
        const current = this.selectedReferral();
        if (current && current.id === referralId) {
          this.selectedReferral.set({
            ...current,
            messages: [...(current.messages || []), message]
          });
        }
      })
    );
  }

  /**
   * Update a message on a referral
   */
  updateMessage(referralId: number, messageId: number, request: UpdateReferralMessageRequest): Observable<ReferralMessage> {
    return this.referralsRepo.updateMessage(referralId, messageId, request).pipe(
      tap(updatedMessage => {
        const current = this.selectedReferral();
        if (current && current.id === referralId) {
          const messages = (current.messages || []).map(m =>
            m.id === messageId ? updatedMessage : m
          );
          this.selectedReferral.set({ ...current, messages });
        }
      })
    );
  }

  /**
   * Load messages for a referral
   */
  loadMessages(referralId: number): Observable<ReferralMessage[]> {
    return this.referralsRepo.getMessages(referralId);
  }

  /**
   * Load snapshots for a referral
   */
  loadSnapshots(referralId: number): Observable<PracticeReferralPatientSnapshot[]> {
    return this.referralsRepo.getSnapshots(referralId).pipe(
      tap(snapshots => {
        this.referralSnapshots.set(snapshots);
      })
    );
  }

  /**
   * Load a single snapshot
   */
  loadSnapshot(referralId: number, snapshotId: number): Observable<PracticeReferralPatientSnapshot> {
    return this.referralsRepo.getSnapshot(referralId, snapshotId);
  }

  /**
   * Create a refer-back snapshot
   */
  createSnapshot(referralId: number, request: CreateReferralSnapshotRequest): Observable<number> {
    return this.referralsRepo.createSnapshot(referralId, request).pipe(
      tap(() => {
        this.loadSnapshots(referralId).subscribe();
      })
    );
  }

  /**
   * Import a snapshot into the patient record
   */
  importSnapshot(referralId: number, snapshotId: number, request: AcceptReferralRequest): Observable<boolean> {
    return this.referralsRepo.importSnapshot(referralId, snapshotId, request).pipe(
      tap(() => {
        this.loadSnapshots(referralId).subscribe();
      })
    );
  }

  /**
   * Setup WebSocket subscriptions for referral events
   */
  setupPushService(): void {
    const channelName = this.practiceEventsChannelName();
    if (!channelName) return;

    this.websocket.subscribe(channelName, Events.ReferralReferralCreated, () => {
      this.loadAll().subscribe();
    });

    this.websocket.subscribe(channelName, Events.ReferralReferralUpdated, () => {
      this.loadAll().subscribe();
    });

    this.websocket.subscribe(channelName, Events.ReferralReferralMessageCreated, (data: any) => {
      // Reload messages for the selected referral if it matches
      const current = this.selectedReferral();
      if (current && data?.referralId === current.id) {
        this.referralsRepo.getMessages(current.id).subscribe(messages => {
          this.selectedReferral.set({ ...current, messages });
        });
      }
    });

    this.websocket.subscribe(channelName, Events.ReferralReferralPatientSnapshotCreated, (data: any) => {
      const current = this.selectedReferral();
      if (current && data?.referralId === current.id) {
        this.loadSnapshots(current.id).subscribe();
      }
    });

    this.websocket.subscribe(channelName, Events.InformationRequestInformationRequestCreated, (data: any) => {
      this.events.trigger(new CoreEvent('information_request_created', data));
      this.coreService.showToast('Emergency information request received', 5000);
    });
  }

  /**
   * Reset WebSocket subscriptions
   */
  resetPushService(): void {
    const channelName = this.practiceEventsChannelName();
    if (!channelName) return;

    this.websocket.unsubscribe(channelName, Events.ReferralReferralCreated);
    this.websocket.unsubscribe(channelName, Events.ReferralReferralUpdated);
    this.websocket.unsubscribe(channelName, Events.ReferralReferralMessageCreated);
    this.websocket.unsubscribe(channelName, Events.ReferralReferralPatientSnapshotCreated);
    this.websocket.unsubscribe(channelName, Events.InformationRequestInformationRequestCreated);
  }

  /**
   * Clear all referral data
   */
  clear(): void {
    this.incomingReferrals.set([]);
    this.outgoingReferrals.set([]);
    this.selectedReferral.set(null);
    this.patientReferrals.set([]);
    this.referralSnapshots.set([]);
    this.referralTargets.set([]);
    this.loaded.set(false);
    this.loading.set(false);
  }

  private updateReferralStatus(id: number, status: ReferralStatus): void {
    // Update in incoming list
    const incoming = this.incomingReferrals();
    const incomingIndex = incoming.findIndex(r => r.id === id);
    if (incomingIndex > -1) {
      const updated = [...incoming];
      updated[incomingIndex] = { ...updated[incomingIndex], status };
      this.incomingReferrals.set(updated);
    }

    // Update in outgoing list
    const outgoing = this.outgoingReferrals();
    const outgoingIndex = outgoing.findIndex(r => r.id === id);
    if (outgoingIndex > -1) {
      const updated = [...outgoing];
      updated[outgoingIndex] = { ...updated[outgoingIndex], status };
      this.outgoingReferrals.set(updated);
    }

    // Update selected referral
    const selected = this.selectedReferral();
    if (selected && selected.id === id) {
      this.selectedReferral.set({ ...selected, status });
    }
  }
}
